import React from 'react'

import {
  Modal,
  ModalBody,
  ModalHeader,
  Button,
  ModalFooter,
  Input,
  Text,
  ModalOverlay,
  ModalContent,
  FormControl,
  FormLabel,
  FormErrorMessage,
  ModalCloseButton,
} from '@chakra-ui/react'
import { useForm } from 'react-hook-form'
import { ModalCancelButton } from 'src/lib/chakra-theme/components'
import { useMirohaToast } from 'src/lib/chakra-theme/components/toast/use-miroha-toast'

import { useDisableWorksheet } from '../../api/disableWorksheet'
import { WorksheetDetail } from '../../types'
import { MAX_TEXT_LENGTH } from '../../utils/validate'

type Props = {
  isOpen: boolean
  isFollowUp: boolean
  worksheet: WorksheetDetail
  onComplete: (worksheet: WorksheetDetail) => void
  onCancel: () => void
}

export const DisableWorksheetModal: React.FC<Props> = ({
  isOpen,
  isFollowUp,
  onCancel,
  onComplete,
  worksheet,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid },
  } = useForm<{
    reason: string
  }>({
    mode: 'onTouched',
  })

  const toast = useMirohaToast()

  const { request: disableWorksheet } = useDisableWorksheet({
    onSuccess: worksheet => {
      onComplete(worksheet)
      toast({
        title: 'ワークシートを無効化しました。',
        status: 'success',
      })
    },
    onError: error => {
      toast({
        title: error.message,
        status: 'error',
      })
    },
  })

  return (
    <Modal onClose={onCancel} isOpen={isOpen} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>
          <Text fontSize="lg" fontWeight="bold">
            ワークシートを無効化
          </Text>
        </ModalHeader>
        <ModalBody>
          <Text>
            {isFollowUp ? (
              <>
                <Text
                  as="span"
                  fontWeight="bold"
                >{`#${worksheet.index} ${worksheet.name}`}</Text>
                <Text
                  mx="1"
                  as="span"
                  fontWeight="bold"
                  fontSize="sm"
                  color="gray.500"
                >
                  {worksheet.schema.name}
                </Text>
              </>
            ) : (
              <Text as="span" fontWeight="bold" mr="1">
                {worksheet.name}
              </Text>
            )}
            <Text as="span">を無効化します。</Text>
          </Text>
          <FormControl
            id="reason"
            mt="6"
            isInvalid={errors['reason']?.type === 'maxLength'}
          >
            <FormLabel>無効化理由</FormLabel>

            <Input
              placeholder="無効化理由を入力してください"
              {...register('reason', {
                required: true,
                maxLength: MAX_TEXT_LENGTH,
              })}
            />

            {errors['reason']?.type === 'maxLength' && (
              <FormErrorMessage color="red.500" fontSize="xs" mt="1.5" mb="0">
                {MAX_TEXT_LENGTH}文字以内で入力してください。
              </FormErrorMessage>
            )}
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <ModalCancelButton />
          <Button
            onClick={handleSubmit(({ reason }) =>
              disableWorksheet({
                worksheetUid: worksheet.uid,
                reason,
              }),
            )}
            isDisabled={!isDirty || !isValid}
          >
            無効化する
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
